import React, { useState, useEffect, useRef } from 'react';
import { FaChevronLeft, FaChevronRight, FaAngleDoubleLeft, FaAngleDoubleRight, FaDownload } from 'react-icons/fa';
import './PhotoViewer.css';
import CategoryNav from './CategoryNav';
import PhotoStage from './PhotoStage';
import { setCachedRatio } from './imageRatioCache';

const importPhotos = () => {
  const context = require.context('./assets/photos', false, /\.(png|jpe?g|gif|webp)$/);
  return context.keys()
    .sort((a, b) => b.localeCompare(a, undefined, { numeric: true }))
    .map(key => ({ src: context(key), filename: key }));
};

const CATEGORIES = [
  { id: 'filmstills', name: 'Film Stills', pattern: /filmstills/i },
  { id: 'neutrals', name: 'Celebration Screening', pattern: /NEUTRALS/i },
  { id: 'bts', name: 'On Set', pattern: /j2bts/i },
];

const computeCategories = (photos) =>
  CATEGORIES
    .map(cat => ({ ...cat, startIndex: photos.findIndex(p => cat.pattern.test(p.filename)) }))
    .filter(cat => cat.startIndex !== -1);

const SKIP_DISTANCE = 10;

// Swipe-to-advance thresholds, now measured against how much of a drag got
// handed off to the track (see resolveDrag below) rather than the raw
// pointer distance — a big pan that never actually reached its edge
// shouldn't register as a swipe attempt at all.
const SWIPE_MIN_DISTANCE = 40;
const SWIPE_MAX_TIME = 400;
const SWIPE_CONFIRM_DISTANCE = 90;

// How long the track's settle animation (commit or snap-back) takes.
// Shared between the actual CSS transition and a JS fallback timer (see
// settleTrack) so a browser that skips the transition — most notably
// prefers-reduced-motion, which forces transition:none via the media
// query below — still reliably finishes the gesture instead of leaving
// trackAnimating stuck true forever.
const SETTLE_MS = 320;

// Stable reference so prev/next PhotoStage instances (which are always "at
// rest") don't re-render on every drag tick just because a fresh {x:0,y:0}
// literal would otherwise look like a new prop each time.
const ZERO_OFFSET = Object.freeze({ x: 0, y: 0 });

// Visible gap between slots on the track, in px. Purely cosmetic breathing
// room so each slot's edge vignette (see .vm-edge-fade in PhotoStage) has
// room to read as a fade rather than a hard cut — the gap itself shows the
// viewer's own background (see the track transform below for how this is
// factored into the slide math, since CSS flex `gap` doesn't change the
// track's own width, only how far its content overflows it).
const TRACK_GAP = 56;

export default function PhotoViewer() {
  const [photos, setPhotos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [index, setIndex] = useState(0);

  // Zoom/pan for the CURRENT photo only — prev/next are always at rest.
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [pinching, setPinching] = useState(false);
  const [lastTouchDist, setLastTouchDist] = useState(null);

  // The sliding track. trackBase is the resting/target page position, in
  // percent of the viewport: -100 shows the current (middle) slot, which
  // is always where it settles; -200/0 are the commit targets for
  // advancing to next/prev. trackDrag is a live pixel amount added on top
  // during an active drag or while settling. trackAnimating toggles the
  // CSS transition — off while live-dragging (1:1 tracking), on while
  // settling to a destination.
  const [trackBase, setTrackBase] = useState(-100);
  const [trackDrag, setTrackDrag] = useState(0);
  const [trackAnimating, setTrackAnimating] = useState(false);
  // Which physical slot (0/1/2 — see the PhotoStage rendering below)
  // currently plays which role. Slot identity is permanent (never keyed by
  // photo index — see PhotoStage.js for why); this is what lets a commit
  // relabel prev/current/next without moving any DOM node. Advancing to
  // 'next' rotates it +1, 'prev' rotates it -1, wrapping mod 3.
  const [slotRotation, setSlotRotation] = useState(0);

  const dragStart = useRef({ x: 0, y: 0 });
  const dragStartTime = useRef(0);
  const panOffsetStart = useRef({ x: 0, y: 0 });
  const scaleRef = useRef(1);
  scaleRef.current = scale;
  const trackDragRef = useRef(0);
  trackDragRef.current = trackDrag;
  // The current photo's rendered frame size, measured once at the start of
  // each drag (transforms don't affect layout, so this stays valid for the
  // whole gesture regardless of zoom).
  const frameSizeRef = useRef({ width: 0, height: 0 });
  const currentMatRef = useRef(null);
  // 'next' | 'prev' | null (null = snapping back to the current photo,
  // no index change once the settle finishes) for whichever settle is
  // currently in flight.
  const pendingDirection = useRef(null);
  const settleTimeoutRef = useRef(null);

  useEffect(() => {
    const loaded = importPhotos();
    setPhotos(loaded);
    setCategories(computeCategories(loaded));
  }, []);

  // Reset zoom/pan whenever the current photo changes, whichever way it
  // changed (swipe/button commit, skip, or category jump).
  useEffect(() => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
    setDragging(false);
    setPinching(false);
    setLastTouchDist(null);
  }, [index]);

  useEffect(() => () => {
    if (settleTimeoutRef.current) clearTimeout(settleTimeoutRef.current);
  }, []);

  // Preload adjacent photos for instant navigation
  // Relies on browser caching, avoiding excessive memory use
  // NOTE: on slow connections, preload requests may compete with current img for bandwidth
  //
  // Setting .src only guarantees the bytes are downloaded — browsers can
  // still defer the actual decode (turning compressed JPEG data into a
  // paintable bitmap) until the image is first drawn, which costs real CPU
  // time for a large photo. Calling .decode() forces that work to happen
  // now, off-screen, so it's already done by the time we navigate.
  useEffect(() => {
    if (photos.length === 0) return;
    const offsets = [-SKIP_DISTANCE, -1, 1, SKIP_DISTANCE];
    const indices = new Set(offsets.map(off => (index + off + photos.length) % photos.length));
    categories.forEach(cat => indices.add(cat.startIndex));
    indices.forEach(i => {
      const src = photos[i].src;
      const img = new Image();
      img.src = src;
      // Also capture this photo's aspect ratio into the shared cache as
      // soon as it's known, ahead of it ever becoming a mounted PhotoStage
      // — this is what lets a skip or category jump land on an
      // already-correctly-shaped frame instead of a placeholder that pops
      // into shape a moment later (see imageRatioCache.js).
      const capture = () => {
        if (img.naturalWidth && img.naturalHeight) {
          setCachedRatio(src, img.naturalWidth / img.naturalHeight);
        }
      };
      if (img.decode) {
        img.decode().then(capture).catch(() => {});
      } else {
        img.onload = capture;
      }
    });
  }, [index, photos, categories]);

  const clamp = (val, min, max) => Math.min(max, Math.max(min, val));

  // Animate the track to a destination: 'next'/'prev' commits to the
  // adjacent photo, null snaps back to the current one. Either way this is
  // the only place that starts a settle animation — swipe release and the
  // prev/next buttons both funnel through it.
  const settleTrack = direction => {
    pendingDirection.current = direction;
    setTrackAnimating(true);
    if (direction === 'next') setTrackBase(-200);
    else if (direction === 'prev') setTrackBase(0);
    setTrackDrag(0);

    if (settleTimeoutRef.current) clearTimeout(settleTimeoutRef.current);
    settleTimeoutRef.current = setTimeout(finishSettle, SETTLE_MS + 60);
  };

  // Called once the settle animation actually finishes — normally via the
  // track's transitionend, but also from a JS timeout fallback in case the
  // transition never fires (prefers-reduced-motion forces transition:none
  // via the media query below, which means no transitionend either).
  // Idempotent: if both call it, the second call is a harmless no-op.
  const finishSettle = () => {
    if (settleTimeoutRef.current) {
      clearTimeout(settleTimeoutRef.current);
      settleTimeoutRef.current = null;
    }
    const direction = pendingDirection.current;
    pendingDirection.current = null;
    setTrackAnimating(false);
    setTrackBase(-100);
    setTrackDrag(0);
    if (direction === 'next') {
      setSlotRotation(r => (r + 1) % 3);
      setIndex(i => (i + 1) % photos.length);
    } else if (direction === 'prev') {
      setSlotRotation(r => (r - 1 + 3) % 3);
      setIndex(i => (i - 1 + photos.length) % photos.length);
    }
  };

  const handleTrackTransitionEnd = e => {
    if (e.target !== e.currentTarget || e.propertyName !== 'transform') return;
    finishSettle();
  };

  // How many inter-slot gaps sit between the track's resting left edge and
  // whichever slot trackBase is currently aiming to show (see the
  // transform above): 0 for the prev slot (trackBase 0, nothing to its
  // left), 1 for the current/middle slot (trackBase -100), 2 for the next
  // slot (trackBase -200).
  const gapStepsFor = base => (base === 0 ? 0 : base === -200 ? 2 : 1);

  const prev = () => { if (!trackAnimating) settleTrack('prev'); };
  const next = () => { if (!trackAnimating) settleTrack('next'); };
  // Skip and category jumps aren't adjacent photos, so they cut instead of
  // sliding through everything in between.
  const skipPrev = () => { if (!trackAnimating) setIndex(i => (i - SKIP_DISTANCE + photos.length) % photos.length); };
  const skipNext = () => { if (!trackAnimating) setIndex(i => (i + SKIP_DISTANCE) % photos.length); };
  const handleJump = i => { if (!trackAnimating) setIndex(i); };

  const downloadCurrent = () => {
    const a = document.createElement('a');
    a.href = photos[index].src;
    a.download = `photo_${index + 1}.jpg`;
    a.click();
  };

  // The photo pans within its own frame until its edge reaches the frame
  // edge — beyond that there's nothing left to reveal by panning further,
  // so any additional drag in that direction is handed off to the track
  // instead ("snagging" the frame along). At scale===1 there's no pan room
  // at all (maxX/maxY are 0), so 100% of the drag goes to the track from
  // the first pixel — the same formula covers both "grabbed the photo,
  // zoomed in" and "grabbed the frame directly" without needing to know
  // which one actually happened.
  const resolveDrag = (desiredX, desiredY) => {
    const { width, height } = frameSizeRef.current;
    const s = scaleRef.current;
    const maxX = Math.max(0, (width * (s - 1)) / 2);
    const maxY = Math.max(0, (height * (s - 1)) / 2);
    const panX = clamp(desiredX, -maxX, maxX);
    const panY = clamp(desiredY, -maxY, maxY);
    return { panX, panY, overflowX: desiredX - panX };
  };

  const startDrag = (x, y) => {
    setDragging(true);
    dragStart.current = { x, y };
    dragStartTime.current = Date.now();
    panOffsetStart.current = { ...offset };
    const rect = currentMatRef.current
      ? currentMatRef.current.getBoundingClientRect()
      : { width: 0, height: 0 };
    frameSizeRef.current = { width: rect.width, height: rect.height };
  };

  const moveDrag = (x, y) => {
    const dx = x - dragStart.current.x;
    const dy = y - dragStart.current.y;
    const { panX, panY, overflowX } = resolveDrag(
      panOffsetStart.current.x + dx,
      panOffsetStart.current.y + dy
    );
    setOffset({ x: panX, y: panY });
    setTrackDrag(overflowX);
  };

  const endDrag = () => {
    setDragging(false);
    const overflow = trackDragRef.current;
    const absOverflow = Math.abs(overflow);
    if (absOverflow === 0) return; // pure pan that never reached its edge — nothing to settle
    const elapsed = Date.now() - dragStartTime.current;
    const fastEnough = elapsed < SWIPE_MAX_TIME && absOverflow > SWIPE_MIN_DISTANCE;
    const farEnough = absOverflow > SWIPE_CONFIRM_DISTANCE;
    if (fastEnough || farEnough) {
      settleTrack(overflow < 0 ? 'next' : 'prev');
    } else {
      settleTrack(null);
    }
  };

  // Mouse wheel to zoom
  const handleWheel = e => {
    e.preventDefault();
    const delta = -e.deltaY * 0.001;
    setScale(s => clamp(s + delta, 0.3, 3));
  };

  const handleMouseDown = e => {
    if (e.button !== 0 || trackAnimating) return;
    e.preventDefault();
    startDrag(e.clientX, e.clientY);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };
  const handleMouseMove = e => {
    moveDrag(e.clientX, e.clientY);
  };
  const handleMouseUp = () => {
    window.removeEventListener('mousemove', handleMouseMove);
    window.removeEventListener('mouseup', handleMouseUp);
    endDrag();
  };

  // Touch gestures for pinch, pan/swipe
  const handleTouchStart = e => {
    if (trackAnimating) return;
    if (e.touches.length === 2) {
      setPinching(true);
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      setLastTouchDist(Math.hypot(dx, dy));
    } else if (e.touches.length === 1 && !pinching) {
      startDrag(e.touches[0].clientX, e.touches[0].clientY);
    }
  };
  const handleTouchMove = e => {
    e.preventDefault();
    if (pinching && e.touches.length === 2 && lastTouchDist) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.hypot(dx, dy);
      setScale(s => clamp(s * (dist / lastTouchDist), 0.3, 3));
      setLastTouchDist(dist);
    } else if (dragging && e.touches.length === 1) {
      moveDrag(e.touches[0].clientX, e.touches[0].clientY);
    }
  };
  const handleTouchEnd = e => {
    if (pinching && e.touches.length < 2) {
      setPinching(false);
      setLastTouchDist(null);
    }
    if (dragging && e.touches.length === 0) {
      endDrag();
    }
  };

  if (photos.length === 0) {
    return <div className="photo-viewer loading">Loading photos...</div>;
  }

  const prevIndex = (index - 1 + photos.length) % photos.length;
  const nextIndex = (index + 1) % photos.length;

  return (
    <div className="photo-viewer">
      <div className="vm-topbar">
        <div className="vm-wordmark">VIEW MASTER <span>MK III</span></div>
        {categories.length > 0 && (
          <CategoryNav categories={categories} currentIndex={index} onJump={handleJump} />
        )}
      </div>

      <div
        className="vm-track-viewport"
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{ touchAction: 'none' }}
      >
        <div
          className="vm-track"
          onTransitionEnd={handleTrackTransitionEnd}
          style={{
            // trackBase alone (-100/0/-200) would be exactly right for
            // three abutting, gap-less 100%-wide slots. With a real gap
            // between them, the resting/committed positions each need an
            // extra pixel nudge for however many gaps sit to their left —
            // 1 gap to reach the middle slot, 2 to reach the far slot, 0
            // for the near one — layered into the same px term trackDrag
            // already uses for live dragging.
            transform: `translateX(calc(${trackBase}% + ${trackDrag - gapStepsFor(trackBase) * TRACK_GAP}px))`,
            transition: trackAnimating ? `transform ${SETTLE_MS}ms cubic-bezier(0.22, 1, 0.36, 1)` : 'none',
            gap: TRACK_GAP
          }}
        >
          {[0, 1, 2].map(slot => {
            // role 0 = prev, 1 = current, 2 = next — see slotRotation above.
            const role = ((slot - slotRotation) % 3 + 3) % 3;
            const isCurrent = role === 1;
            const photoIndex = role === 0 ? prevIndex : role === 1 ? index : nextIndex;
            return (
              <PhotoStage
                key={slot}
                order={role}
                photo={photos[photoIndex]}
                isCurrent={isCurrent}
                scale={isCurrent ? scale : 1}
                offset={isCurrent ? offset : ZERO_OFFSET}
                dragging={isCurrent ? dragging : undefined}
                matRef={isCurrent ? currentMatRef : undefined}
              />
            );
          })}
        </div>
      </div>

      <div className="photo-controls">
        <div className="photo-controls-tray">
          <button onClick={skipPrev} className="control-button" aria-label="Skip back 10 photos">
            <FaAngleDoubleLeft />
          </button>
          <button onClick={prev} className="control-button" aria-label="Previous photo">
            <FaChevronLeft />
          </button>
          <span className="photo-counter">
            {index + 1} / {photos.length}
          </span>
          <button onClick={next} className="control-button" aria-label="Next photo">
            <FaChevronRight />
          </button>
          <button onClick={skipNext} className="control-button" aria-label="Skip forward 10 photos">
            <FaAngleDoubleRight />
          </button>
          <button onClick={downloadCurrent} className="control-button" aria-label="Download photo">
            <FaDownload />
          </button>
        </div>
      </div>
    </div>
  );
}
